/**
 * The "Add to Home Screen" bar, at the top of the bottom banner stack (`BannerStack`).
 *
 * Installing used to mean finding Chrome's ⋮ menu and the right item in it, which most people
 * never do. This puts the offer where they already are: one tap on Chromium (the real install
 * dialog), and on iOS, where no page can install anything, the two taps spelled out.
 *
 * What to offer is `installOffer` in `install.ts`, tested as a platform table. This file only
 * renders it and remembers "Cancel" for a day.
 */
import { useEffect, useMemo, useState } from "react";
import {
  installBarSnoozed,
  installOfferHere,
  onInstallPromptChange,
  promptInstall,
  snoozeInstallBar,
  type InstallOffer,
} from "../install.ts";
import { localStore } from "../localStore.ts";

export function InstallBanner(): React.ReactElement | null {
  const store = useMemo(() => localStore(), []);
  const [offer, setOffer] = useState<InstallOffer>(() => installOfferHere());
  const [snoozed, setSnoozed] = useState(() => installBarSnoozed(store, Date.now()));

  // The prompt can arrive after mount, and is used up once shown.
  useEffect(() => onInstallPromptChange(() => setOffer(installOfferHere())), []);

  if (offer === null || snoozed) return null;

  const dismiss = (): void => {
    snoozeInstallBar(store, Date.now());
    setSnoozed(true);
  };

  const install = async (): Promise<void> => {
    const outcome = await promptInstall();
    if (outcome === "dismissed") dismiss();
    setOffer(installOfferHere());
  };

  return (
    <div className="install-banner" role="region" aria-label="Install the app">
      <img className="install-banner__icon" src="/icons/icon-96.0641f51c.png" alt="" width={32} height={32} />
      <span className="install-banner__text">
        <b>Add CricAI to your home screen</b>
        <span className="ds-caption">
          {offer === "prompt" ? "Opens like an app, works offline." : null}
          {offer === "ios" ? (
            <>Tap <ShareIcon /> Share, then “Add to Home Screen”.</>
          ) : null}
          {offer === "android-menu" ? <>Tap ⋮ in your browser, then “Add to Home screen”.</> : null}
        </span>
      </span>
      <span className="install-banner__actions">
        <button className="ds-btn ds-btn--ghost ds-btn--sm" type="button" onClick={dismiss}>
          Cancel
        </button>
        {offer === "prompt" ? (
          <button className="ds-btn ds-btn--primary ds-btn--sm" type="button" onClick={() => void install()}>
            Install
          </button>
        ) : null}
      </span>
    </div>
  );
}

function ShareIcon(): React.ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      aria-hidden="true"
      style={{ verticalAlign: "-2px", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" }}
    >
      <path d="M12 3v12M8 7l4-4 4 4" />
      <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
    </svg>
  );
}
